'use client';

/**
 * Client-side refresh for layout codes on SPA navigation.
 *
 * The root layout doesn't re-render on Next.js soft navigation, so the
 * `LayoutCodeSSR*` markup stays frozen from the initial page load.
 * Page-type-specific layout codes (e.g. articlePage* variants) won't be
 * present when the user navigates from the home page to an article via a
 * client-side `<Link>`.
 *
 * This component watches `pathname` and, after the FIRST mount (which is
 * already handled by SSR), tears down the existing layout-code DOM +
 * googletag slots and re-injects the layout codes that apply to the new
 * pathname. Initial mount is a no-op — SSR is the source of truth there.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { getActiveLayoutCodes, isMobileFromUA, type ActiveLayoutCode } from '@/lib/layout-codes-helpers';
interface Props {
  /**
   * All layout codes from the server (active + inactive — filtering happens
   * here per-pathname because the active set changes with the page type).
   */
  allLayoutCodes: Record<string, any>;
  /** pathname → page_id mapping used by specificPageCodes matching. */
  routePageIds: Record<string, string>;
}
function stripEnableServices(html: string): string {
  return html.replace(/googletag\.enableServices\s*\(\s*\)\s*;?/g, '/* enableServices centralised */');
}
function destroyTrackedSlots() {
  const w = window as any;
  const containers = document.querySelectorAll('[data-layout-code-ssr-container]');
  const slotDivIds: string[] = [];
  containers.forEach(c => {
    c.querySelectorAll('[id^="div-gpt-ad-"]').forEach(d => slotDivIds.push((d as HTMLElement).id));
  });
  if (slotDivIds.length > 0 && w.googletag?.pubads) {
    w.googletag.cmd = w.googletag.cmd || [];
    w.googletag.cmd.push(() => {
      try {
        const slots = w.googletag.pubads().getSlots().filter((s: any) => slotDivIds.includes(s.getSlotElementId()));
        if (slots.length > 0) w.googletag.destroySlots(slots);
      } catch {/* ignore */}
    });
  }
}
function removeSSRElements() {
  document.querySelectorAll('[data-layout-code-ssr]').forEach(el => el.remove());
  document.querySelectorAll('[data-layout-code-ssr-container]').forEach(el => el.remove());
}

/**
 * Recursively clone source children into `dest`, re-creating every <script>
 * via createElement so it actually executes when dest is in the live DOM
 * (scripts inserted via innerHTML/template are "parser-inserted" and never
 * run after being moved).
 */
function cloneInto(dest: ParentNode, source: ParentNode, markTop: boolean) {
  Array.from(source.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'SCRIPT') {
      const old = node as HTMLScriptElement;
      const ns = document.createElement('script');
      for (let i = 0; i < old.attributes.length; i++) {
        ns.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      if (old.textContent) ns.textContent = old.textContent;
      ns.setAttribute('data-layout-code-ssr', 'true');
      dest.appendChild(ns);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const shallow = el.cloneNode(false) as Element;
      if (markTop) shallow.setAttribute('data-layout-code-ssr', 'true');
      dest.appendChild(shallow);
      // Recurse into children — never re-mark non-top nodes (keeps the
      // SSR cleanup query simple: only top-level elements carry the attr).
      cloneInto(shallow, el, false);
    } else if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE) {
      dest.appendChild(node.cloneNode(false));
    }
  });
}
function injectHtmlIntoTarget(html: string, target: HTMLElement) {
  if (!html.trim()) return;
  const cleaned = stripEnableServices(html);
  const tpl = document.createElement('template');
  tpl.innerHTML = cleaned;
  cloneInto(target, tpl.content, true);
}
function injectLayoutCodes(codes: ActiveLayoutCode[]) {
  if (codes.length === 0) return;

  // Body container — recreate the wrapper so `[data-layout-code-ssr-container]`
  // queries on the next nav find it for cleanup.
  const bodyContainer = document.createElement('div');
  bodyContainer.setAttribute('data-layout-code-ssr-container', 'true');
  bodyContainer.style.display = 'contents';
  for (const code of codes) {
    if (code.headerCode) injectHtmlIntoTarget(code.headerCode, document.head);
    if (code.bodyCode) injectHtmlIntoTarget(code.bodyCode, bodyContainer);
  }
  document.body.appendChild(bodyContainer);

  // Centralised enableServices: queued AFTER every defineSlot push so
  // every slot activates together. GPT processes cmd in order.
  const w = window as any;
  w.googletag = w.googletag || {
    cmd: []
  };
  w.googletag.cmd.push(() => {
    try {
      w.googletag.enableServices();
    } catch {/* ignore */}
  });
}
export default function LayoutCodeRefresh(t0) {
  const $ = _c(5);
  const {
    allLayoutCodes,
    routePageIds
  } = t0;
  const pathname = usePathname();
  const lastPathRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== allLayoutCodes || $[1] !== pathname || $[2] !== routePageIds) {
    t1 = () => {
      if (lastPathRef.current === null) {
        lastPathRef.current = pathname;
        return;
      }
      if (lastPathRef.current === pathname) {
        return;
      }
      lastPathRef.current = pathname;
      destroyTrackedSlots();
      removeSSRElements();
      const isMobile = typeof window !== "undefined" && (window.matchMedia("(max-width: 767px)").matches || isMobileFromUA(navigator.userAgent));
      const codes = getActiveLayoutCodes(allLayoutCodes, pathname, isMobile, routePageIds);
      injectLayoutCodes(codes);
    };
    t2 = [pathname, allLayoutCodes, routePageIds];
    $[0] = allLayoutCodes;
    $[1] = pathname;
    $[2] = routePageIds;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}